import { JuloUtils } from "../common/JuloUtils"
import { Product } from "../pos"
import { Discount, DiscountKind, hasDiscount } from "./discount"
import { DiscountExclusions, markDiscountExclusions } from "./discount-code"
import { ProductChoice } from "./evaluated-service-bike-part"
import { FreshServiceProposal } from "./fresh-service-proposal"
import {
  Receipt,
  ReceiptManualAction,
  ReceiptPackage,
  ReceiptPartAction,
  ReceiptProduct,
} from "./receipt"

export const SERVICE_VAT = 12

/** Shown instead of a number that doesn't exist, rather than "NaN kr". */
export const MISSING_VALUE = "–"

export class PriceUtils {
  static getNetPrice(price: number, vat: number = SERVICE_VAT): number {
    const net = price / (1 + vat / 100)
    return Number.parseFloat(net.toFixed(2))
  }

  static getGrossPrice(priceExclVat: number, vat: number): number {
    return Math.round(priceExclVat * (1 + vat / 100) * 100) / 100
  }

  static getPriceExcludingVat(grossPrice: number, vat: number): number {
    return Math.round((grossPrice / (1 + vat / 100)) * 100) / 100
  }

  static format(price: number): string {
    if (!Number.isFinite(price)) return MISSING_VALUE
    return `${PriceUtils.formatWithoutCurrency(price)} kr`
  }
  static formatWithoutCurrency(price: number): string {
    return PriceUtils.formatHelper(price, "sv-SE", { style: "decimal" })
  }
  /** Always two decimals ("0,20 kr", "10,00 kr") — for bookkeeping surfaces
   * where öre precision matters, unlike `format` which drops ",00". */
  static formatWithOre(price: number): string {
    return `${PriceUtils.formatHelper(price, "sv-SE", {
      style: "decimal",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} kr`
  }

  /**
   * Rounds a price to the nearest value ending in 9 (e.g., 19, 29, 39, 49, etc.)
   * This is a common pricing strategy in Swedish retail ("charm pricing").
   *
   * Examples:
   * - 47 → 49
   * - 51 → 49
   * - 55 → 59
   * - 100 → 99
   *
   * @param rawPrice The unrounded price
   * @returns The price rounded to nearest "9 kr", minimum 0
   */
  static roundToNearestNine(rawPrice: number): number {
    const roundedPrice = Math.round((rawPrice - 9) / 10) * 10 + 9
    return Math.max(0, roundedPrice)
  }

  private static formatHelper(
    price: number,
    locale: Intl.LocalesArgument = "sv-SE",
    options: Intl.NumberFormatOptions = {
      style: "currency",
      currency: "SEK",
      minimumFractionDigits: 0,
    },
  ) {
    if (!Number.isFinite(price)) return MISSING_VALUE
    return Intl.NumberFormat(locale, options).format(price)
  }

  /**
   * Returns how many kr of a package's labor are replaced by included
   * material. The package price covers material up to `includedMaterialCost`,
   * so products added to the package move that share of the price from labor
   * to products: a 399 kr package with 99 kr of included products is 300 kr
   * labor + 99 kr products.
   *
   * Capped at the product cost (only material actually used moves) and at the
   * labor price (labor never goes negative). Returns 0 when the package has no
   * material allowance.
   *
   * The labor cap is an accepted trade-off: when the allowance exceeds the
   * labor after the per-package discount, the package total ends up above the
   * package price (399 kr, 300 kr allowance, 300 kr products, −50 % → 0 kr
   * labor + 300 kr products, not 200 kr). Price entries are validated so
   * `includedMaterialCost <= price`, which rules this out without a discount.
   *
   * @param laborPrice - the package labor after its own per-package discount
   */
  static getMaterialLaborDeduction(
    totalProductCost: number,
    includedMaterialCost: number | null,
    laborPrice: number,
  ): number {
    if (includedMaterialCost == null || includedMaterialCost <= 0) return 0
    if (totalProductCost <= 0 || laborPrice <= 0) return 0
    return Math.min(totalProductCost, includedMaterialCost, laborPrice)
  }

  /**
   * The labor a receipt package actually charges: its (per-package
   * discounted) labor price minus the share replaced by included material.
   * Every labor sum and labor display for a package goes through this.
   */
  static getPackageLaborPrice(pkg: ReceiptPackage): number {
    return (
      (pkg.laborPriceAfterDiscount ?? pkg.laborPrice) -
      pkg.materialLaborDeduction
    )
  }

  /**
   * The struck-through labor shown next to a package's own labor discount.
   * The included-material deduction is taken from the discounted labor, so it
   * is scaled back up to list-price level here: the original, the discount
   * percent and `getPackageLaborPrice` then reconcile (400 kr, −50 %, 100 kr
   * material → 200 kr, −50 %, 100 kr). The deduction is capped by the
   * rounded discounted labor, so scaling it back up can overshoot the list
   * price; the result is clamped so the struck-through price never goes
   * negative.
   *
   * The ratio is taken from the amounts rather than `laborPriceDiscount`,
   * since `applyOrderDiscountToReceipt` drops the percent but keeps both
   * labor prices.
   */
  static getPackageOriginalLaborPrice(pkg: ReceiptPackage): number {
    const discounted = pkg.laborPriceAfterDiscount
    if (
      discounted == null ||
      discounted <= 0 ||
      pkg.laborPrice <= 0 ||
      discounted >= pkg.laborPrice
    ) {
      return pkg.laborPrice - pkg.materialLaborDeduction
    }
    return Math.max(
      0,
      Math.round(
        pkg.laborPrice -
          (pkg.materialLaborDeduction * pkg.laborPrice) / discounted,
      ),
    )
  }

  /**
   * Single source of truth for package total calculation. Used by both
   * `getProposalPrices` (proposal-side preview) and the receipt mapper so
   * customer-facing and receipt totals never drift.
   *
   * `effectiveLaborPrice` is the labor after the per-package discount and
   * after the included-material deduction.
   */
  static getPackageTotal(args: {
    laborPrice: number
    laborPriceDiscount: Discount | null
    totalProductCost: number
    includedMaterialCost: number | null
  }): {
    total: number
    materialLaborDeduction: number
    effectiveLaborPrice: number
  } {
    const discountedLaborPrice = PriceUtils.applyDiscount(
      args.laborPrice,
      args.laborPriceDiscount,
    )
    const materialLaborDeduction = PriceUtils.getMaterialLaborDeduction(
      args.totalProductCost,
      args.includedMaterialCost,
      discountedLaborPrice,
    )
    const effectiveLaborPrice = discountedLaborPrice - materialLaborDeduction
    const total = effectiveLaborPrice + args.totalProductCost
    return { total, materialLaborDeduction, effectiveLaborPrice }
  }

  static getProposalPrices(proposal: FreshServiceProposal) {
    const { packages, manualActions, partActions, wash, fees } = proposal

    let other = 0
    let feeCost = 0
    let washCost = 0
    let packagesCost = 0

    for (const pkg of packages) {
      let pkgProductCost = 0
      for (const partAction of pkg.partActions) {
        pkgProductCost += PriceUtils.getProductsCost(partAction.products)
        pkgProductCost += PriceUtils.getRecommendedProductsCost(
          partAction.productChoices,
        )
      }
      const { total } = PriceUtils.getPackageTotal({
        laborPrice: pkg.laborPrice,
        laborPriceDiscount: pkg.laborPriceDiscount,
        totalProductCost: pkgProductCost,
        includedMaterialCost: pkg.includedMaterialCost,
      })
      packagesCost += total
    }

    for (const fee of fees) feeCost += fee.cost

    for (const msa of manualActions) {
      other += PriceUtils.applyDiscount(msa.laborPrice, msa.laborPriceDiscount)
      other += PriceUtils.getProductsCost(msa.products)
      other += PriceUtils.getRecommendedProductsCost(msa.productChoices)
    }

    let washExistInPackage = false
    const allPartsInSomePackage = packages.flatMap((pkg) => {
      if (pkg.wash) washExistInPackage = true
      return pkg.partActions.map((partAction) => partAction.partId)
    })

    if (wash && !washExistInPackage) washCost += PriceUtils.getWashPrice(wash)
    const evaluatedPartsNotIncludedInPackage = partActions.filter(
      (part) => !allPartsInSomePackage.includes(part.partId),
    )

    for (const part of evaluatedPartsNotIncludedInPackage) {
      other += PriceUtils.applyDiscount(
        part.laborPrice,
        part.laborPriceDiscount,
      )
      other += PriceUtils.getProductsCost(part.products)
      other += PriceUtils.getRecommendedProductsCost(part.productChoices)
    }

    return {
      total: packagesCost + other + feeCost + washCost,
      other: other,
      fee: feeCost,
      wash: washCost,
    }
  }

  static getProposalPriceLabels(proposal: FreshServiceProposal) {
    const { wash, packages } = proposal as FreshServiceProposal
    const prices = PriceUtils.getProposalPrices(proposal)

    const totalLabel = PriceUtils.format(prices.total)
    const otherLabel = PriceUtils.format(prices.other)
    const feeLabel = PriceUtils.format(prices.fee)
    const washLabel = PriceUtils.format(prices.wash)
    const washExistInSomePackage = packages.some(
      (pkg) => pkg.wash?.snapshotWashId === wash?.snapshotWashId,
    )

    return {
      total: totalLabel,
      other: prices.other === 0 ? null : otherLabel,
      fee: prices.fee > 0 ? feeLabel : null,
      wash: wash && !washExistInSomePackage ? washLabel : null,
    }
  }

  /** What a wash costs after its own per-line discount. */
  static getWashPrice(wash: {
    price: number
    laborPriceDiscount: Discount | null
  }): number {
    return PriceUtils.applyDiscount(wash.price, wash.laborPriceDiscount)
  }

  /**
   * A price after a discount. A percent is rounded to whole kr; a kr amount
   * is subtracted and never takes the price below 0.
   */
  static applyDiscount(
    price: number,
    discount: Discount | null | undefined,
  ): number {
    if (!hasDiscount(discount)) return price
    if (discount.kind === DiscountKind.AMOUNT) {
      return Math.max(0, price - discount.value)
    }
    return Math.round(price * (1 - discount.value / 100))
  }

  /**
   * The share of `base` that remains after `discount` (1 when nothing is
   * taken off). Used to spread a kr discount on a total over its lines.
   */
  static remainingFactor(
    base: number,
    discount: Discount | null | undefined,
  ): number {
    if (base <= 0 || !hasDiscount(discount)) return 1
    return PriceUtils.applyDiscount(base, discount) / base
  }

  /**
   * Applies a discount given on a total (`base`) to one of its lines. A
   * percent is applied to the line as is; a kr amount is spread over the
   * lines in proportion to their price.
   */
  static applyDiscountToLine(
    price: number,
    discount: Discount | null | undefined,
    base: number,
  ): number {
    if (!hasDiscount(discount)) return price
    if (discount.kind === DiscountKind.PERCENT) {
      return PriceUtils.applyDiscount(price, discount)
    }
    return Math.round(price * PriceUtils.remainingFactor(base, discount))
  }

  /** "20 %" or "49 kr". */
  static formatDiscountValue(discount: Discount): string {
    if (discount.kind === DiscountKind.AMOUNT) {
      return PriceUtils.format(discount.value)
    }
    return `${discount.value} %`
  }

  /** "-20%" or "-49 kr" — the badge shown next to a discounted price. */
  static formatDiscountLabel(discount: Discount): string {
    if (discount.kind === DiscountKind.AMOUNT) {
      return `-${PriceUtils.format(discount.value)}`
    }
    return `-${discount.value}%`
  }

  /**
   * Renders a package price for customer-facing surfaces (booking
   * confirmation message, store notification email, marketplace summary).
   * Shows the discounted price alongside the original when a discount
   * applies (`"450 kr, ord. 500 kr"`), otherwise just the price.
   */
  static formatPriceWithDiscount(
    price: number,
    priceAfterDiscount: number | null,
  ): string {
    if (priceAfterDiscount == null || priceAfterDiscount >= price) {
      return JuloUtils.formatSimplePrice(price)
    }
    return `${JuloUtils.formatSimplePrice(priceAfterDiscount)}, ord. ${JuloUtils.formatSimplePrice(price)}`
  }

  /**
   * Calculates the discounted price when a discount is present.
   * Returns null if no discount applies (null, undefined, or a 0 value).
   * This is used in repositories where laborPriceAfterDiscount should only be
   * set when there's an actual discount applied.
   */
  static getDiscountedPriceOrNull(
    originalPrice: number | null,
    discount: Discount | null | undefined,
  ): number | null {
    if (originalPrice == null || !hasDiscount(discount)) return null
    return PriceUtils.applyDiscount(originalPrice, discount)
  }

  /**
   * A row discount as it should be saved: a kr amount never exceeds the
   * row's price, so the stored discount always matches what is charged. A kr
   * amount capped to 0 is no discount at all.
   */
  static clampDiscount(
    discount: Discount | null | undefined,
    price: number,
  ): Discount | null {
    if (discount == null) return null
    if (discount.kind === DiscountKind.PERCENT) return discount
    const value = Math.min(discount.value, Math.max(0, price))
    // Capped to nothing (a free row): there is no discount left to give.
    if (value <= 0) return null
    return { kind: DiscountKind.AMOUNT, value }
  }

  /**
   * Bakes the job-level discounts (receipt.laborDiscount,
   * receipt.productsDiscount and `orderDiscount`) into every line of a
   * receipt, so the discounts show up per line and not just in the total.
   *
   * Mirrors how the job's receipt computes its total: the category discount
   * is applied first and the order discount on top (sequentially, never
   * combined into one rounded percentage). Labor-bearing lines are package,
   * part action and manual action labor, wash and fees. Product line
   * discounts (`product.discount`) are kept; a kr one is scaled together
   * with the product price.
   *
   * The job rounds once on its totals while this rounds per line, so any
   * leftover rounding difference is put on the largest labor line. That way
   * the result always totals the job's `totalCostAfterOrderDiscount`.
   */
  static applyOrderDiscountToReceipt(
    receipt: Receipt,
    orderDiscount: Discount | null,
  ): Receipt {
    const laborDiscount = receipt.laborDiscount ?? null
    const productsDiscount = receipt.productsDiscount ?? null

    if (
      !hasDiscount(laborDiscount) &&
      !hasDiscount(productsDiscount) &&
      !hasDiscount(orderDiscount)
    ) {
      return receipt
    }

    // A kr discount is spread over the lines it applies to, in proportion to
    // their price, so the bases are the totals the job's receipt discounts.
    const { laborCost, productsCost } = PriceUtils.getReceiptCostSums(receipt)
    const orderBase =
      PriceUtils.applyDiscount(laborCost, laborDiscount) +
      PriceUtils.applyDiscount(productsCost, productsDiscount)

    const applyLabor = (price: number): number =>
      PriceUtils.applyDiscountToLine(
        PriceUtils.applyDiscountToLine(price, laborDiscount, laborCost),
        orderDiscount,
        orderBase,
      )

    const applyProduct = (price: number): number =>
      PriceUtils.applyDiscountToLine(
        PriceUtils.applyDiscountToLine(price, productsDiscount, productsCost),
        orderDiscount,
        orderBase,
      )

    // A kr row discount is taken off the row before the job-level discounts,
    // so it is scaled together with the price. A percent is kept as is.
    const discountProducts = <T extends ReceiptProduct>(products: T[]): T[] =>
      products.map((p) => ({
        ...p,
        price: applyProduct(p.price),
        discount:
          p.discount?.kind === DiscountKind.AMOUNT
            ? {
                kind: DiscountKind.AMOUNT,
                value: applyProduct(p.discount.value),
              }
            : p.discount,
      }))

    // Apply discount to packages. The included-material deduction is part of
    // the package labor, so it is scaled together with it.
    const packages = receipt.packages.map((pkg) => {
      if (PriceUtils.isExcludedFromJobDiscounts(pkg)) return pkg
      const discountedProducts = discountProducts(pkg.products)
      const totalProductCost = PriceUtils.getProductsCost(discountedProducts)
      const discountedPackage = {
        ...pkg,
        laborPrice: applyLabor(pkg.laborPrice),
        laborPriceAfterDiscount:
          pkg.laborPriceAfterDiscount != null
            ? applyLabor(pkg.laborPriceAfterDiscount)
            : null,
        // Discount baked into laborPrice; drop the % so it isn't re-applied.
        laborPriceDiscount: null,
        products: discountedProducts,
        totalProductCost,
        materialLaborDeduction: applyLabor(pkg.materialLaborDeduction),
      }
      return {
        ...discountedPackage,
        totalCost:
          PriceUtils.getPackageLaborPrice(discountedPackage) + totalProductCost,
      }
    })

    const discountAction = <T extends ReceiptPartAction | ReceiptManualAction>(
      action: T,
    ): T => {
      const discountedLaborPrice = applyLabor(action.laborPrice)
      const discountedProducts = discountProducts(action.products)
      const totalProductCost = PriceUtils.getProductsCost(discountedProducts)
      return {
        ...action,
        laborPrice: discountedLaborPrice,
        laborPriceOriginal: applyLabor(action.laborPriceOriginal),
        laborPriceAfterDiscount:
          action.laborPriceAfterDiscount != null
            ? applyLabor(action.laborPriceAfterDiscount)
            : null,
        // Discount baked into laborPrice; drop the % so it isn't re-applied.
        laborPriceDiscount: null,
        products: discountedProducts,
        totalProductCost,
        totalCost: discountedLaborPrice + totalProductCost,
      }
    }

    const discounted: Receipt = {
      ...receipt,
      packages,
      partActions: receipt.partActions.map((action) =>
        PriceUtils.isExcludedFromJobDiscounts(action)
          ? action
          : discountAction(action),
      ),
      manualActions: receipt.manualActions.map((action) =>
        PriceUtils.isExcludedFromJobDiscounts(action)
          ? action
          : discountAction(action),
      ),
      wash: receipt.wash
        ? {
            ...receipt.wash,
            price: applyLabor(receipt.wash.price),
            priceOriginal: applyLabor(receipt.wash.priceOriginal),
          }
        : null,
      fees: receipt.fees.map((fee) => ({
        ...fee,
        price: applyLabor(fee.price),
      })),
    }

    const reconciled =
      receipt.totalCostAfterOrderDiscount != null
        ? PriceUtils.absorbRoundingDifference(
            discounted,
            receipt.totalCostAfterOrderDiscount -
              PriceUtils.getReceiptLinesTotal(discounted),
          )
        : discounted
    const totalCost = PriceUtils.getReceiptLinesTotal(reconciled)

    return {
      ...reconciled,
      totalCost,
      orderDiscount,
      // Discounts are now baked into the line prices, so consumers reading
      // line-by-line must not re-apply them. Null these out to make that
      // contract explicit.
      laborDiscount: null,
      laborCostAfterDiscount: null,
      productsDiscount: null,
      productsCostAfterDiscount: null,
      totalCostAfterOrderDiscount: totalCost,
      vat: Math.round(totalCost * (SERVICE_VAT / 100) * 100) / 100,
    }
  }

  /** Sum of every line's total (per-package totalCost already accounts for
   * the included-material deduction). */
  private static getReceiptLinesTotal(receipt: Receipt): number {
    return (
      receipt.packages.reduce((sum, pkg) => sum + pkg.totalCost, 0) +
      receipt.partActions.reduce((sum, pa) => sum + pa.totalCost, 0) +
      receipt.manualActions.reduce((sum, ma) => sum + ma.totalCost, 0) +
      (receipt.wash?.price ?? 0) +
      receipt.fees.reduce((sum, fee) => sum + fee.price, 0)
    )
  }

  /**
   * Adds `diff` kr to the labor of the line with the largest labor, so a
   * receipt discounted line by line totals exactly what the job's receipt
   * (which rounds once, on its totals) says. Falls back to the largest
   * single product when no labor line can take `diff` without going
   * negative; no-op when neither exists.
   */
  private static absorbRoundingDifference(
    receipt: Receipt,
    diff: number,
  ): Receipt {
    if (diff === 0) return receipt

    const actionLabor = (a: ReceiptPartAction | ReceiptManualAction): number =>
      a.laborPriceAfterDiscount ?? a.laborPriceOriginal
    const candidates: { labor: number; apply: () => Receipt }[] = []

    const adjustAction = <T extends ReceiptPartAction | ReceiptManualAction>(
      action: T,
    ): T => ({
      ...action,
      laborPrice: action.laborPrice + diff,
      laborPriceOriginal:
        action.laborPriceAfterDiscount == null
          ? action.laborPriceOriginal + diff
          : action.laborPriceOriginal,
      laborPriceAfterDiscount:
        action.laborPriceAfterDiscount != null
          ? action.laborPriceAfterDiscount + diff
          : null,
      totalCost: action.totalCost + diff,
    })

    // Lines kept out of the job's discounts keep their exact price.
    receipt.partActions.forEach((pa, i) => {
      if (PriceUtils.isExcludedFromJobDiscounts(pa)) return
      candidates.push({
        labor: actionLabor(pa),
        apply: () => ({
          ...receipt,
          partActions: receipt.partActions.map((a, j) =>
            j === i ? adjustAction(a) : a,
          ),
        }),
      })
    })
    receipt.manualActions.forEach((ma, i) => {
      if (PriceUtils.isExcludedFromJobDiscounts(ma)) return
      candidates.push({
        labor: actionLabor(ma),
        apply: () => ({
          ...receipt,
          manualActions: receipt.manualActions.map((a, j) =>
            j === i ? adjustAction(a) : a,
          ),
        }),
      })
    })
    receipt.packages.forEach((pkg, i) => {
      if (PriceUtils.isExcludedFromJobDiscounts(pkg)) return
      candidates.push({
        labor: PriceUtils.getPackageLaborPrice(pkg),
        apply: () => ({
          ...receipt,
          packages: receipt.packages.map((p, j) =>
            j === i
              ? {
                  ...p,
                  laborPrice:
                    p.laborPriceAfterDiscount == null
                      ? p.laborPrice + diff
                      : p.laborPrice,
                  laborPriceAfterDiscount:
                    p.laborPriceAfterDiscount != null
                      ? p.laborPriceAfterDiscount + diff
                      : null,
                  totalCost: p.totalCost + diff,
                }
              : p,
          ),
        }),
      })
    })
    receipt.fees.forEach((fee, i) => {
      candidates.push({
        labor: fee.price,
        apply: () => ({
          ...receipt,
          fees: receipt.fees.map((f, j) =>
            j === i ? { ...f, price: f.price + diff } : f,
          ),
        }),
      })
    })
    const wash = receipt.wash
    if (wash) {
      candidates.push({
        labor: wash.price,
        apply: () => ({
          ...receipt,
          wash: { ...wash, price: wash.price + diff },
        }),
      })
    }

    const largestLabor = candidates.reduce<(typeof candidates)[number] | null>(
      (best, c) =>
        c.labor > 0 && c.labor + diff >= 0 && (!best || c.labor > best.labor)
          ? c
          : best,
      null,
    )
    if (largestLabor) return largestLabor.apply()

    // No labor line can take it (e.g. a parts-only job): adjust a single
    // product instead. Only products bought once and without a line discount
    // qualify, so the line total moves by exactly `diff` kr.
    const productCandidates: { price: number; apply: () => Receipt }[] = []
    const collectProducts = (
      products: ReceiptProduct[],
      applyAt: (productIndex: number) => Receipt,
    ): void => {
      products.forEach((product, k) => {
        if (product.quantity !== 1 || hasDiscount(product.discount)) return
        if (product.price <= 0 || product.price + diff < 0) return
        productCandidates.push({
          price: product.price,
          apply: () => applyAt(k),
        })
      })
    }
    const adjustProduct = <
      T extends {
        products: ReceiptProduct[]
        totalProductCost: number
        totalCost: number
      },
    >(
      line: T,
      productIndex: number,
    ): T => ({
      ...line,
      products: line.products.map((p, k) =>
        k === productIndex ? { ...p, price: p.price + diff } : p,
      ),
      totalProductCost: line.totalProductCost + diff,
      totalCost: line.totalCost + diff,
    })

    receipt.partActions.forEach((pa, i) => {
      if (PriceUtils.isExcludedFromJobDiscounts(pa)) return
      collectProducts(pa.products, (k) => ({
        ...receipt,
        partActions: receipt.partActions.map((a, j) =>
          j === i ? adjustProduct(a, k) : a,
        ),
      }))
    })
    receipt.manualActions.forEach((ma, i) => {
      if (PriceUtils.isExcludedFromJobDiscounts(ma)) return
      collectProducts(ma.products, (k) => ({
        ...receipt,
        manualActions: receipt.manualActions.map((a, j) =>
          j === i ? adjustProduct(a, k) : a,
        ),
      }))
    })
    receipt.packages.forEach((pkg, i) => {
      if (PriceUtils.isExcludedFromJobDiscounts(pkg)) return
      collectProducts(pkg.products, (k) => ({
        ...receipt,
        packages: receipt.packages.map((p, j) =>
          j === i ? adjustProduct(p, k) : p,
        ),
      }))
    })

    const largestProduct = productCandidates.reduce<
      (typeof productCandidates)[number] | null
    >((best, c) => (!best || c.price > best.price ? c : best), null)
    return largestProduct ? largestProduct.apply() : receipt
  }

  /**
   * Total products cost across a receipt: package, standalone part action
   * and manual action products. Included material is not subtracted — it
   * lowers the package labor instead (see `getPackageLaborPrice`).
   */
  static getReceiptTotalProductsCost(receipt: Receipt): number {
    const packagesCost = receipt.packages.reduce(
      (sum, pkg) => sum + pkg.totalProductCost,
      0,
    )
    const partActionsCost = receipt.partActions.reduce(
      (sum, pa) => sum + pa.totalProductCost,
      0,
    )
    const manualActionsCost = receipt.manualActions.reduce(
      (sum, ma) => sum + ma.totalProductCost,
      0,
    )
    return packagesCost + partActionsCost + manualActionsCost
  }

  static getProductsCost(
    products: {
      price: number
      quantity?: number
      discount?: Discount | null
    }[],
  ): number {
    return products.reduce((acc, curr) => {
      const basePrice = curr.price * (curr.quantity ?? 1)
      const discount = curr.discount ?? null
      return acc + PriceUtils.applyDiscount(basePrice, discount)
    }, 0)
  }

  static getRecommendedProductsCost(productChoices: ProductChoice[]): number {
    return productChoices.reduce((acc, choice) => {
      const rec = choice.recommendedAlternative
      const basePrice = rec.price * (rec.quantity ?? 1)
      const discount = rec.discount ?? null
      return acc + PriceUtils.applyDiscount(basePrice, discount)
    }, 0)
  }

  static flattenProductChoices(productChoices: ProductChoice[]): Product[] {
    return productChoices.flatMap((choice) => [
      choice.recommendedAlternative,
      ...choice.otherAlternatives,
    ])
  }

  static getCombinedProductsCost(
    products: Product[] = [],
    productChoices: ProductChoice[] = [],
  ): number {
    const allProducts = [
      ...products,
      ...PriceUtils.flattenProductChoices(productChoices),
    ]
    return PriceUtils.getProductsCost(allProducts)
  }

  /**
   * Total cost for a single action (manual service action or part action),
   * combining labor with the cost of the action's products and the
   * user-selected (or recommended) alternative from each product choice.
   *
   * Returns both the discounted `total` (the headline price) and the
   * pre-discount `rawTotal` (used for the strike-through "before discount"
   * label). Computing both in one pass guarantees they stay in sync, instead
   * of the previous approach where the strike-through value tried to
   * reverse-engineer itself from the discounted total and only ever undid
   * the labor discount — losing every per-product discount.
   *
   * Used by the `useTotalActionPrice` hook to render the action's headline
   * price, and by tests asserting that price is correct.
   */
  static calculateActionTotalCost(input: {
    laborPrice?: number
    laborPriceDiscount?: Discount | null
    products: Product[]
    productChoices: ProductChoice[]
    productDecisions?: Record<string, string>
  }): { total: number; rawTotal: number } {
    const laborPrice = input.laborPrice ?? 0
    const discountedLaborPrice = PriceUtils.applyDiscount(
      laborPrice,
      input.laborPriceDiscount ?? null,
    )
    let total = discountedLaborPrice
    let rawTotal = laborPrice

    for (const product of input.products) {
      const grossProductPrice = product.price * (product.quantity ?? 1)
      total += PriceUtils.applyDiscount(
        grossProductPrice,
        product.discount ?? null,
      )
      rawTotal += grossProductPrice
    }

    for (const choiceGroup of input.productChoices) {
      let selectedProduct = choiceGroup.recommendedAlternative

      if (input.productDecisions?.[choiceGroup.id]) {
        const selectedProductId = input.productDecisions[choiceGroup.id]
        const foundProduct = [
          choiceGroup.recommendedAlternative,
          ...choiceGroup.otherAlternatives,
        ].find((product) => product.id === selectedProductId)

        if (foundProduct) {
          selectedProduct = foundProduct
        }
      }

      const grossSelectedPrice =
        selectedProduct.price * (selectedProduct.quantity ?? 1)
      total += PriceUtils.applyDiscount(
        grossSelectedPrice,
        selectedProduct.discount ?? null,
      )
      rawTotal += grossSelectedPrice
    }

    return { total, rawTotal }
  }

  /**
   * Whether the job's labor, products and order discounts skip this line. An
   * upsell bought in the proposal's "Tillval" step already carries its own
   * "here and now" discount, and a discount code can exclude packages and
   * part actions (`markDiscountExclusions`).
   */
  static isExcludedFromJobDiscounts(line: {
    isExcludedFromJobDiscounts?: boolean
    isUpsell?: boolean
  }): boolean {
    return line.isExcludedFromJobDiscounts === true || line.isUpsell === true
  }

  /**
   * Labor (incl. wash and fees) and products of the lines `isIncluded` keeps,
   * before the job-level discounts. Per-row discounts are included.
   */
  private static sumReceiptCosts(
    receipt: Pick<
      Receipt,
      "packages" | "partActions" | "manualActions" | "wash" | "fees"
    >,
    isIncluded: (line: {
      isExcludedFromJobDiscounts?: boolean
      isUpsell?: boolean
    }) => boolean,
    includeWashAndFees: boolean,
  ): { laborCost: number; productsCost: number } {
    const packages = receipt.packages.filter(isIncluded)
    const partActions = receipt.partActions.filter(isIncluded)
    const manualActions = receipt.manualActions.filter(isIncluded)
    const washAndFeesCost = includeWashAndFees
      ? (receipt.wash ? receipt.wash.price : 0) +
        receipt.fees.reduce((sum, fee) => sum + fee.price, 0)
      : 0

    const laborCost =
      packages.reduce(
        (sum, pkg) => sum + PriceUtils.getPackageLaborPrice(pkg),
        0,
      ) +
      partActions.reduce(
        (sum, pa) => sum + (pa.laborPriceAfterDiscount ?? pa.laborPrice),
        0,
      ) +
      manualActions.reduce((sum, ma) => sum + ma.laborPrice, 0) +
      washAndFeesCost

    const productsCost =
      packages.reduce((sum, pkg) => sum + pkg.totalProductCost, 0) +
      partActions.reduce((sum, pa) => sum + pa.totalProductCost, 0) +
      manualActions.reduce((sum, ma) => sum + ma.totalProductCost, 0)

    return { laborCost, productsCost }
  }

  /**
   * The labor and products totals job-level discounts (labor, products,
   * order) apply to. Lines excluded from them (`isExcludedFromJobDiscounts`)
   * are left out — add `getExcludedFromJobDiscountsCost` to get the full
   * total.
   */
  static getReceiptCostSums(
    receipt: Pick<
      Receipt,
      "packages" | "partActions" | "manualActions" | "wash" | "fees"
    >,
  ): { laborCost: number; productsCost: number } {
    return PriceUtils.sumReceiptCosts(
      receipt,
      (line) => !PriceUtils.isExcludedFromJobDiscounts(line),
      true,
    )
  }

  /**
   * Labor and products of the lines job-level discounts never touch, so
   * they are added on top of the discounted totals.
   */
  static getExcludedFromJobDiscountsCostSums(
    receipt: Pick<
      Receipt,
      "packages" | "partActions" | "manualActions" | "wash" | "fees"
    >,
  ): { laborCost: number; productsCost: number } {
    return PriceUtils.sumReceiptCosts(
      receipt,
      PriceUtils.isExcludedFromJobDiscounts,
      false,
    )
  }

  /** What the lines job-level discounts never touch cost in total. */
  static getExcludedFromJobDiscountsCost(
    receipt: Pick<
      Receipt,
      "packages" | "partActions" | "manualActions" | "wash" | "fees"
    >,
  ): number {
    const { laborCost, productsCost } =
      PriceUtils.getExcludedFromJobDiscountsCostSums(receipt)
    return laborCost + productsCost
  }

  /**
   * Applies labor, products, and order discounts to a receipt, leaving out
   * the lines `exclusions` (and upsells) keep at full price.
   * Returns a new receipt with all discount fields populated.
   */
  static applyDiscountsToReceipt(
    unmarkedReceipt: Receipt,
    discounts: {
      laborDiscount?: Discount | null
      productsDiscount?: Discount | null
      orderDiscount?: Discount | null
      exclusions?: DiscountExclusions
    },
  ): Receipt {
    const { laborDiscount, productsDiscount, orderDiscount, exclusions } =
      discounts
    const receipt = exclusions
      ? markDiscountExclusions(unmarkedReceipt, exclusions)
      : unmarkedReceipt

    const { laborCost: totalLaborCost, productsCost: totalProductsCost } =
      PriceUtils.getReceiptCostSums(receipt)
    const { laborCost: excludedLaborCost, productsCost: excludedProductsCost } =
      PriceUtils.getExcludedFromJobDiscountsCostSums(receipt)

    const discountableLaborAfterDiscount = PriceUtils.applyDiscount(
      totalLaborCost,
      laborDiscount,
    )
    const discountableProductsAfterDiscount = PriceUtils.applyDiscount(
      totalProductsCost,
      productsDiscount,
    )
    const laborCostAfterDiscount = hasDiscount(laborDiscount)
      ? discountableLaborAfterDiscount + excludedLaborCost
      : null

    const productsCostAfterDiscount = hasDiscount(productsDiscount)
      ? discountableProductsAfterDiscount + excludedProductsCost
      : null

    const discountableAfterItemDiscounts =
      discountableLaborAfterDiscount + discountableProductsAfterDiscount
    const excludedCost = excludedLaborCost + excludedProductsCost
    const totalAfterItemDiscounts =
      discountableAfterItemDiscounts + excludedCost

    // Apply order discount on top
    const totalCostAfterOrderDiscount = hasDiscount(orderDiscount)
      ? PriceUtils.applyDiscount(
          discountableAfterItemDiscounts,
          orderDiscount,
        ) + excludedCost
      : null

    return {
      ...receipt,
      laborDiscount: hasDiscount(laborDiscount) ? laborDiscount : null,
      laborCostAfterDiscount,
      productsDiscount: hasDiscount(productsDiscount) ? productsDiscount : null,
      productsCostAfterDiscount,
      orderDiscount: hasDiscount(orderDiscount) ? orderDiscount : null,
      totalCost: totalAfterItemDiscounts,
      totalCostAfterOrderDiscount,
    }
  }
}
